import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import { atomWithDefault } from "jotai/utils";
import { isEqual, omit } from "lodash-es";
import { config } from "../../config";
import { SelfDrivingService } from "../../server/api/self-driving";
import { getTokenAtom } from "../../server/auth/store";
import { authHeader } from "../../utils/auth-header";
import { loadable } from "../../utils/jotai-loadable";
import { brandingAtom, brandingImageTypeAtom } from "../brand-kit/store";
import { withContentTracking } from "../content-generation/track-content-generation";
import { brandInfoToCustomizations } from "../design-huddle/brand-info-customizations/brand-info-customizations";
import { imageURL } from "../design-huddle/utils";
import { huddle } from "../huddle/huddle";
import { selfDriving } from "../self-driving/self-driving";
import { isContextQuery } from "./types";

import type { UUID } from "../../utils/uuid";
import type { BrandTone } from "../brand-kit/server/brand-tone-service";
import type { TemplateInfo } from "../brand-kit/types";
import type { Channel, TemplateContent } from "../generic/atoms/types/template";
import type { ToneType } from "../self-driving/types";
import type { ContextQuery, GenerateQuery, RecipeQuery, TemplateInitialContext } from "./types";

export const accountIdAtom = atom<UUID | null>(null);

const promptAtom = atom<string | null>(null);

const templateQueryAtom = atom(
  (get): RecipeQuery | null => {
    const prompt = get(promptAtom);
    return prompt ? { recipeId: "default", answers: [prompt] } : null;
  },
  (get, set, update: string) => set(promptAtom, update),
);

const recipeIdAtom = atom<string | null>(null);
const answersAtom = atom<string[]>([]);

const recipeQueryAtom = atom(
  (get): RecipeQuery | null => {
    const recipeId = get(recipeIdAtom);
    const answers = get(answersAtom);
    return recipeId && answers.length > 0 ? { recipeId, answers } : null;
  },
  (get, set, update: RecipeQuery | null) => {
    if (update === null) {
      set(recipeIdAtom, null);
      set(answersAtom, []);
      return;
    }
    set(recipeIdAtom, update.recipeId);
    set(answersAtom, update.answers);
  },
);

const contextQueryAtom = atom<ContextQuery | null>(null);

export const queryAtom = atom<GenerateQuery | null>(
  (get) => get(contextQueryAtom) ?? get(templateQueryAtom) ?? get(recipeQueryAtom),
);

const templateInitialContextAtom = atomWithDefault<Promise<TemplateInitialContext | null>>(
  async (get) => {
    const q = get(queryAtom);

    const token = await get(getTokenAtom).getToken();

    if (!token || !q) return null;

    if (isContextQuery(q)) {
      return q;
    }

    return null;
  },
);

export const debugLabel = <T>(l: string, a: T): T => {
  // @ts-ignore
  a.debugLabel = l;
  return a;
};

export const templateContentFamily = atomFamily(
  ({
    contentKey,
    query,
    customTone,
  }: {
    contentKey: ToneType;
    query?: GenerateQuery | null;
    customTone?: BrandTone | null;
  }) =>
    debugLabel(
      `templateContentFamily(${query?.recipeId}-${contentKey}${customTone ? `-${customTone.name}` : ""})`,
      atomWithDefault(async (get): Promise<TemplateContent | null> => {
        const i = await get(templateInitialContextAtom);
        const token = await get(getTokenAtom).getToken();

        if (!i || !token || !query || (contentKey === "custom" && !customTone)) return null;

        const { data, contentGenerationId } = await withContentTracking(
          token,
          {
            contentType: "moment",
            subType: "recipe_text",
            prompt: JSON.stringify({
              chatId: i.chatId,
              ...("answers" in query && { answers: query.answers }),
              tone: contentKey === "custom" ? customTone!.summary : contentKey,
            }),
          },
          async () => {
            const { data: result, error } = await selfDriving.POST(
              "/recipes/{recipeId}/follow_up/text",
              {
                body: {
                  chatId: i.chatId,
                  ...("answers" in query && { answers: query.answers.map((a) => ({ answer: a })) }),
                  ...(contentKey === "custom"
                    ? { tone: "custom", toneSummary: customTone!.summary }
                    : { tone: contentKey }),
                },
                params: {
                  path: { recipeId: query.recipeId },
                  query: { model: config.gpt_model },
                },
                ...authHeader(token),
              },
            );

            if (error || !result) throw new Error(error.error || "Request failed");
            return result;
          },
        );

        return { type: "generated" as const, data, contentGenerationId };
      }),
    ),
  isEqual,
);

export const brandToneAtom = atom<BrandTone | null>(null);
export const channelsAtom = atom<Channel[]>([]);

export const multiformatPhotosFamily = atomFamily(
  ({
    formatId,
    description,
    industry,
    db,
    fileId,
  }: {
    formatId: string;
    description: string;
    industry: string;
    db?: string;
    fileId?: string;
  }) =>
    debugLabel(
      `multiformatPhotosFamily(${formatId}, ${description})`,
      atomWithDefault(async (get) => {
        const token = await get(getTokenAtom).getToken();

        if (!token) return undefined;

        const service = new SelfDrivingService(token);
        return (await service.photos(formatId, description, undefined, industry, fileId, db)).data
          ?.photos;
      }),
    ),
  isEqual,
);

export const multiformatIconsFamily = atomFamily(
  ({ formatId, description, fileId }: { formatId: string; description: string; fileId?: string }) =>
    debugLabel(
      `multiformatIconsFamily(${formatId}, ${description})`,
      atomWithDefault(async (get) => {
        const token = await get(getTokenAtom).getToken();
        if (!token) return undefined;

        const service = new SelfDrivingService(token);
        return (await service.icons(formatId, description, fileId)).data?.icons;
      }),
    ),
  isEqual,
);

export const multiformatVideosFamily = atomFamily(
  ({ formatId, description, fileId }: { formatId: string; description: string; fileId?: string }) =>
    debugLabel(
      `multiformatVideosFamily(${formatId}, ${description})`,
      atomWithDefault(async (get) => {
        const token = await get(getTokenAtom).getToken();
        if (!token) return undefined;

        const service = new SelfDrivingService(token);
        return (await service.videos(formatId, description, fileId)).data?.videos;
      }),
    ),
  isEqual,
);

export const imagePreviewDimensions = atom((get) => {
  const image_type = get(brandingImageTypeAtom);

  return image_type === "banner"
    ? { width: 1200, height: 400, image_type: "banner" }
    : { width: 1200, height: 1200, image_type: "square" };
});

const imagePreviewURLFamily = atomFamily(
  (info: TemplateInfo) =>
    debugLabel(
      `imagePreviewURLFamily(${JSON.stringify(info)})`,
      atomWithDefault(
        (get) =>
          new Promise<string>((resolve, reject) => {
            const customizations = brandInfoToCustomizations({
              brandInfoSelected: get(brandingAtom),
              templateInfo: info,
            });

            if (info.dhProject) {
              const account_id = get(accountIdAtom);
              if (account_id) {
                resolve(
                  `${imageURL(account_id, info.dhProject.project_id)}?c=${new Date().getTime()}`,
                );
              } else {
                resolve(
                  info.dhProject.pages.find((page) => page.page_number === info.page_number)
                    ?.thumbnail_url ?? "",
                );
              }
            }

            const dimensions = omit(get(imagePreviewDimensions), "image_type");

            return DSHDLib.getVariableTemplatePreviewURL(
              {
                template_code: info.template_code,
                customizations,
                page_number: info.page_number,
                ...dimensions,
              },
              (err, url) => {
                if (err) reject(err);
                resolve(url);
              },
            );
          }),
      ),
    ),
  isEqual,
);

/**
 * `imagePreviewURLFamily` is only ever read as a loadable, so the wrapper lives
 * here rather than at the call site — one derived atom per key, created once.
 */
export const loadableImagePreviewURLFamily = atomFamily(
  (info: TemplateInfo) => loadable(imagePreviewURLFamily(info)),
  isEqual,
);

export const existingProjectFamily = atomFamily((projectId: string | null | undefined) =>
  debugLabel(
    `existingProjectPagesFamily(${projectId})`,
    atom(async (get) => {
      if (!projectId) return undefined;

      const accessToken = await getDesignHuddleAccessToken([projectId], get(getTokenAtom).getToken);

      const projectResp = await fetch(
        `https://${config.design_huddle.domain}/api/projects/${projectId}`,
        {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          headers: { authorization: `Bearer ${accessToken}` },
        },
      );
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const { success: projectSuccess, data: projectData } = (await projectResp.json()) as {
        success: boolean;
        data: {
          pages: { page_id: string; page_number: number }[];
          dimensions: { width: number; height: number };
        };
      };

      if (!projectSuccess || !projectData) throw new Error("Project Error");

      return { pages: projectData.pages, dimensions: projectData.dimensions, projectId };
    }),
  ),
);

async function getDesignHuddleAccessToken(
  projects: string[],
  getToken: () => Promise<string | null>,
): Promise<string> {
  const response = await huddle.POST("/", {
    body: { projects },
    headers: { Authorization: `Bearer ${await getToken()}` },
  });
  return response.data!.access_token;
}
